import { Injectable } from '@angular/core';
import { Province } from '@interfaces/province';
import { License } from '@interfaces/rest/interfaces/licenses';
import { PaginationParams } from '@interfaces/rest/interfaces/paginationParams';
import { ServiceInfo, Zone, ZoneInfo } from '@interfaces/rest/interfaces/zone';
import { User } from '@interfaces/user';
import { BehaviorSubject } from 'rxjs';
import { RestService } from './rest.service';

@Injectable({
  providedIn: 'root'
})
export class ProvinceService {
  zoneInfo: ZoneInfo = {
    drivers: [],
    zones: []
  };
  provinceId?: number;
  province?: Province;
  allProvinces: Province[] = [];

  updateZoneInfo: BehaviorSubject<any> = new BehaviorSubject(null);
  zoneInfo$: BehaviorSubject<ZoneInfo | null> = new BehaviorSubject<ZoneInfo | null>(null);
  serviceInfo$: BehaviorSubject<ServiceInfo | null> = new BehaviorSubject<ServiceInfo | null>(null);

  lastZoneUpdate = new Date();

  licenseList: License[] = [];
  driversList: User[] = [];

  constructor(private rest: RestService) {
  }

  async getZonesFromApi(provinceId: number, isAdmin = false) {
    const resp = await this.rest.getZoneInfoByProvinceId(provinceId);
    if (resp.ok && resp.response?.zoneInfo) {
      if (!isAdmin) {
        await this.getLicenseList();
        this.zoneInfo.zones = resp.response.zoneInfo.zones;
        // if (!this.zoneInfo.drivers.length && resp.response.zoneInfo.drivers) {
        //   this.zoneInfo.drivers = resp.response.zoneInfo.drivers;
        // }
      } else {
        const provinceZones = resp.response.zoneInfo.zones.filter((zone: Zone) => zone.isProvince);
        this.zoneInfo.zones = this.zoneInfo.zones.concat(provinceZones);
        // if (!this.zoneInfo.drivers.length && resp.response.zoneInfo.drivers) {
        //   this.zoneInfo.drivers = this.zoneInfo.drivers.concat(resp.response.zoneInfo.drivers);
        // }
      }
    }
  }

  async getAllProvincesData() {
    if (!this.allProvinces.length) {
      const result = await this.rest.getProvincesAll();
      if (result.ok) {
        this.allProvinces = result.response;
      }
    }
  }

  async getProvinceName(provinceId: number) {
    await this.getAllProvincesData();
    const province = this.allProvinces.find(elem => elem.id === provinceId);
    console.log('province', province);
    return province ? province.name : '';
  }

  async getAllProvinces() {
    const result = await this.rest.getProvincesAll();
    if (result.ok) {
      this.allProvinces = result.response;
      if (this.allProvinces.length) {
        for (const province of this.allProvinces) {
          if (province.id) {
            await this.getZonesFromApi(province.id, true);
          }
        }
        await this.getLicenseList(true);
        this.zoneInfo$.next(this.zoneInfo);
      }
    }
  }

  async getLicenseList(isAdmin = false) {
    const result = await this.rest.getLicensesAll({ limit: 250 });
    if (result.ok) {
      const initLicenseList = result.response?.data?.result || [];
      if (!this.provinceId) {
        await this.getSetProvinceId();
      }
      if (isAdmin) {
        this.licenseList = initLicenseList;
      } else {
        this.licenseList = initLicenseList.filter(elem => elem.provinceId?.id === this.provinceId);
      }
    }
  }

  async getSetProvinceId() {
    const result = await this.rest.getCurrentUser();
    if (result?.province?.id) {
      this.provinceId = result.province.id;
    }
  }

  async getLicenses() {
    if (this.licenseList.length === 0) {
      await this.getLicenseList();
    }
    return this.licenseList;
  }

  async getDriversList() {
    if (this.driversList.length === 0) {
      const params: PaginationParams = {
        limit: 700,
        'where.role': 'driver',
        'where.province.id': this.provinceId
      };
      const result = await this.rest.getUsersAll(params);
      if (result.ok) {
        this.driversList = result.response.data.result;
      }
    }
    return this.driversList;
  }

  async getZones(): Promise<ZoneInfo | undefined> {
    if (this.zoneInfo?.zones.length) {
      return this.zoneInfo;
    } else {
      if (this.provinceId) {
        await this.getZonesFromApi(this.provinceId);
      } else {
        const user = await this.rest.getCurrentUser();
        if (user?.province) {
          this.provinceId = user.province.id;
          return this.getZones();
        }
        if (user?.role?.includes('admin')) {
          await this.getAllProvinces();
        }
      }
      return this.zoneInfo;
    }
  }

  async updateZone(zoneInfo: ZoneInfo) {
    if (this.zoneInfo) {
      this.zoneInfo.drivers = zoneInfo.drivers ?? [];
      this.zoneInfo.drivers.forEach(elem => {
        const findLicense = this.licenseList.find(elemLL => elemLL.id === elem.l);
        if (findLicense) {
          elem.license = findLicense;
        }
      });

      this.zoneInfo$.next(this.zoneInfo);

      if (this.provinceId) {
        const fiveMinutes = 24 * 60 * 60 * 1000;
        if (new Date().getTime() - this.lastZoneUpdate.getTime() > fiveMinutes) {
          this.lastZoneUpdate = new Date();
          await this.getZonesFromApi(this.provinceId);
        }
      }
    }
    if (!this.zoneInfo.zones.length) {
      await this.getZones();
    }
  }

  updateService(serviceInfo: ServiceInfo, type: string) {
    let oldServiceInfo = this.serviceInfo$.getValue();
    if (!oldServiceInfo) {
      oldServiceInfo = {
        zi: [],
        s: 0, ss: 0
      };
    }
    if (type === 'zone') {
      oldServiceInfo.zi = serviceInfo.zi;
    } else {
      oldServiceInfo.s = serviceInfo.s;
      oldServiceInfo.ss = serviceInfo.ss;
    }
    this.serviceInfo$.next(oldServiceInfo);
  }

  async getDrivers() {
    if (!this.zoneInfo.zones.length) {
      await this.getZones();
    }
    return this.zoneInfo?.drivers;
  }

  async getZoneNameById(id: string) {
    await this.getZones();
    const zoneInfo = this.zoneInfo?.zones.find(zone => zone?._id === id);
    return zoneInfo ? (zoneInfo.name || '') : '';
  }

  async getProvince() {
    if (!this.province) {
      if (!this.provinceId) {
        await this.getSetProvinceId();
      }
      if (this.provinceId) {
        const result = await this.rest.getProvinceById(this.provinceId!);
        if (result.ok) {
          this.province = result.response;
        }
      }
    }
    return this.province;
  }

  async getCenterMap() {
    const defaultCenter = {
      lat: 39.846371,
      lng: 3.124781
    }

    if (!this.province) {
      await this.getProvince();
    }

    if (this.province?.config?.centerMap) {
      return this.province?.config?.centerMap;
    } else {
      return defaultCenter;
    }

  }

}
